import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { PageLoader } from '../components/LoadingSpinner';
import { dividendsAPI } from '../lib/api';
import { formatNumber, formatDate } from '../lib/formatters';
import { TrendingUp, Calendar, DollarSign, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function DividendsPage() {
  const [dividends, setDividends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDividends();
  }, []);

  const fetchDividends = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dividendsAPI.getMy();
      setDividends(response.data);
    } catch (error) {
      console.error('Failed to fetch dividends:', error);
      setError('Unable to load dividend history. Please try again.');
      toast.error('Failed to load dividends');
    } finally {
      setLoading(false);
    }
  };

  const totalEarned = dividends.reduce((sum, div) => sum + div.user_amount, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <Header />
        <PageLoader text="Loading dividends..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-700 font-medium mb-2">Unable to load dividends</p>
            <p className="text-red-600 text-sm mb-4">{error}</p>
            <button
              onClick={fetchDividends}
              className="inline-flex items-center gap-2 px-6 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Header />

      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[hsl(var(--text-primary))] mb-2">
            Dividend History
          </h1>
          <p className="text-[hsl(var(--text-secondary))]">
            Track earnings from your asset ownership
          </p>
        </div>

        {/* Total Earned Card */}
        <div className="bg-gradient-to-br from-[hsl(var(--gain))] to-green-600 rounded-xl p-8 mb-8 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp className="w-8 h-8 text-white" />
            <span className="text-white text-lg font-medium">Total Dividends Earned</span>
          </div>
          <div className="text-5xl md:text-6xl font-bold text-white font-mono">
            {formatNumber(totalEarned)}
          </div>
          <div className="text-white/80 text-sm mt-2">HUB Coins</div>
        </div>

        {/* Dividends List */}
        <div>
          <h2 className="text-2xl font-semibold text-[hsl(var(--text-primary))] mb-4">
            Payment History
          </h2>

          {dividends.length === 0 ? (
            <div className="bg-white border border-[hsl(var(--border))] rounded-xl p-12 text-center">
              <DollarSign className="w-12 h-12 text-[hsl(var(--text-tertiary))] mx-auto mb-4" />
              <p className="text-[hsl(var(--text-secondary))]">No dividends received yet</p>
              <p className="text-sm text-[hsl(var(--text-tertiary))] mt-2">
                Dividends are distributed when assets generate revenue
              </p>
            </div>
          ) : (
            <div data-testid="dividends-list" className="grid grid-cols-1 gap-4">
              {dividends.map((dividend) => (
                <div
                  key={dividend.dividend_id}
                  data-testid={`dividend-${dividend.dividend_id}`}
                  className="bg-white border border-[hsl(var(--border))] rounded-xl p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Left: Asset Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-[hsl(var(--gain-light))] rounded-lg">
                          <TrendingUp className="w-5 h-5 text-[hsl(var(--gain))]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-[hsl(var(--text-primary))] text-lg">
                            {dividend.asset_name}
                          </h3>
                          <div className="flex items-center gap-2 text-sm text-[hsl(var(--text-secondary))]">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(dividend.distribution_date)}</span>
                            <span className="px-2 py-0.5 bg-[hsl(var(--background-secondary))] rounded text-xs font-medium">
                              {dividend.period}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Calculation Breakdown */}
                      <div className="mt-3 p-3 bg-[hsl(var(--background-secondary))] rounded-lg">
                        <div className="text-sm text-[hsl(var(--text-secondary))] space-y-1">
                          <div>Your Shares: <span className="font-mono font-semibold text-[hsl(var(--text-primary))]">{dividend.user_shares}</span></div>
                          <div>Coins per Share: <span className="font-mono font-semibold text-[hsl(var(--text-primary))]">{formatNumber(dividend.coins_per_share)}</span></div>
                        </div>
                      </div>
                    </div>

                    {/* Right: Amount */}
                    <div className="text-right">
                      <div className="text-sm text-[hsl(var(--text-secondary))] mb-1">You Received</div>
                      <div className="text-4xl font-bold font-mono text-[hsl(var(--gain))]">
                        +{formatNumber(dividend.user_amount)}
                      </div>
                      <div className="text-sm text-[hsl(var(--text-tertiary))] mt-1">Coins</div>
                    </div>
                  </div>

                  {/* Notes */}
                  {dividend.notes && (
                    <div className="mt-4 pt-4 border-t border-[hsl(var(--border))]">
                      <div className="text-sm text-[hsl(var(--text-secondary))]">
                        <span className="font-medium">Note:</span> {dividend.notes}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
